import { CommonModule } from '@angular/common';
import {
  Component,
  OnInit,
  OnDestroy,
  DoCheck,
  ElementRef,
  ViewChild,
  HostListener,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { OverrideStyleConfig } from '@pv-frontend/pv-shared-components/common/types';
import { DeviceService } from '@pv-frontend/pv-shared-services/device-service';
import { SidebarModule } from 'primeng/sidebar';
import { TooltipModule } from 'primeng/tooltip';
import { SearchBarComponent } from 'projects/visa-platform/src/app/shared/components/search-bar/search-bar.component';
import { SignupComponent } from 'projects/visa-platform/src/app/shared/components/signup/signup.component';
import { AuthService } from 'projects/visa-platform/src/app/shared/services/auth.service';
import { LanguageService } from 'projects/visa-platform/src/app/shared/services/language.service';
import { ModalService } from 'projects/visa-platform/src/app/shared/services/modal.service';
import { RazorpayAnalyticsService } from 'projects/visa-platform/src/app/shared/services/razorpay-analytics.service';
import { StorageService } from 'projects/visa-platform/src/app/shared/services/storage.service';
import { environment } from 'projects/visa-platform/src/environments/environment';
import { Environment } from 'projects/visa-platform/src/environments/environment.interface';
import { Subject, filter, takeUntil } from 'rxjs';

import { SSOService } from '../../services/sso.service';

@Component({
  selector: 'visa-platform-header',
  imports: [
    SignupComponent,
    CommonModule,
    SearchBarComponent,
    FormsModule,
    TooltipModule,
    SidebarModule,
  ],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, OnDestroy, DoCheck {
  @ViewChild('dropdownRef') private dropdownRef!: ElementRef;

  public isUserLoggedIn: boolean = false;
  public showAuthModal: boolean = false;
  private destroyed$: Subject<boolean> = new Subject();
  public showSearchIcon: boolean = false;
  public env: Environment = environment;
  public languages = this.env.languages ?? [];
  public selectedLanguage: string = 'en';
  public languageDropdownClicked: boolean = false;
  public isMobile = this.deviceService.isCurrentDeviceMobile();
  public sidebarVisible: boolean = false;
  public mobileIconStyle: OverrideStyleConfig = {
    width: '24px',
    height: 'auto',
  };
  public isSSOFlow: boolean = false;

  public constructor(
    private router: Router,
    private authService: AuthService,
    public modalService: ModalService,
    private analyticsService: RazorpayAnalyticsService,
    private deviceService: DeviceService,
    private ssoService: SSOService,
    private languageService: LanguageService,
    private storageService: StorageService
  ) {}

  public ngOnInit(): void {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      // eslint-disable-next-line
      .subscribe((event: any) => {
        this.showSearchIcon = this.shouldShowSearch(event.urlAfterRedirects);
      });
    this.authService.authModalSubject$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ visible }) => {
        this.showAuthModal = visible;
      });
    this.checkAuth();
    this.ssoService.ssoFlow$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isSSOFlow) => {
        this.isSSOFlow = isSSOFlow;
      });
    const savedLanguage = this.languageService.currentLang;
    const isValidLang = this.languages.some(
      (lang) => lang.code === savedLanguage
    );
    this.selectedLanguage = isValidLang ? savedLanguage! : 'en';
    this.languageService.languageChanged$.subscribe(() => {
      this.languages = this.env.languages ?? [];
    });
  }

  public get selectedLanguageLabel(): string {
    const langObj = this.languages?.find(
      (lang) => lang.code === this.selectedLanguage
    );
    return langObj ? langObj.label : '';
  }

  private shouldShowSearch(url: string): boolean {
    return url.startsWith('/offer') || url === '/';
  }

  public ngDoCheck(): void {
    this.checkAuth();
  }

  private checkAuth(): void {
    this.isUserLoggedIn = this.authService.isAuthenticated();
  }

  public showLoginModal(activeScreen: string): void {
    this.authService.showModal(activeScreen);
  }

  public logout(): void {
    if (environment.razorAnalyticsFlag) {
      this.analyticsService.removeIdentity();
    }
    this.authService.deleteToken().subscribe({
      next: () => {
        this.clearSessionAndNavigate();
      },
      error: () => {
        this.clearSessionAndNavigate();
      },
    });
  }

  private clearSessionAndNavigate(): void {
    this.storageService.clearLocalStorage();
    this.storageService.clearSessionStorage();
    this.router.navigate(['/']);
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public openSearchModal(): void {
    this.modalService.showSearchBarModal();
  }

  public navigateTo(url: string): void {
    this.router.navigate([url]);
  }

  public navigateToHistory(): void {
    const historyRoute = this.env.tierGamificationFlag
      ? 'milestone-history'
      : 'order-history';
    this.router.navigate([historyRoute]);
  }

  public onLanguageChange(langCode: string): void {
    this.selectedLanguage = langCode;
    const selectedLang = this.languages.find((lang) => lang.code === langCode);
    this.languageService.setCurrLang({
      code: langCode,
      label: selectedLang?.label || '',
      id: selectedLang?.id || '',
      locale: selectedLang?.locale || '',
    });
    this.languageService.setLanguageTexts();
    this.languageDropdownClicked = false;
    window.location.reload();
  }

  @HostListener('document:click', ['$event'])
  public onDocumentClick(event: MouseEvent): void {
    if (
      this.languageDropdownClicked &&
      this.dropdownRef &&
      !this.dropdownRef.nativeElement.contains(event.target)
    ) {
      this.languageDropdownClicked = false;
    }
  }
}
